import { useLayoutEffect, useRef, useState } from 'react';
import { useResultData } from '../../context';
import { tokens } from '../../tokens';

interface Props {
  width?: number;
  height?: number;
}

/** Fraction of each bin's slot the bar fills — the rest is breathing room. */
const BAR_FILL = 0.7;

const fmtBinLabel = (start: number, end: number): string => {
  const mid = (start + end) / 2;
  const pct = Math.round(mid * 100);
  return `${pct >= 0 ? '+' : ''}${pct}%`;
};

const ProfitHistogram = ({ width = 540, height = 200 }: Props) => {
  const { histogram } = useResultData();
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Render at the container's true width so the chart fills the card instead
  // of being letterboxed inside a fixed-width viewBox.
  const [renderWidth, setRenderWidth] = useState(width);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const element = containerRef.current;
    const measure = () => setRenderWidth(element.clientWidth || width);
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [width]);

  const content = (() => {
    if (!histogram.length) {
      return null;
    }
    const bars = histogram.filter((bin) => bin.count > 0);
    const maxCount = Math.max(...bars.map((bin) => bin.count));
    const padLeft = 8;
    const padRight = 8;
    const padTop = 24;
    const padBottom = 28;
    const chartWidth = renderWidth - padLeft - padRight;
    const chartHeight = height - padTop - padBottom;
    const slotWidth = bars.length ? chartWidth / bars.length : chartWidth;
    const barWidth = slotWidth * BAR_FILL;

    return (
      <>
        <line
          x1={padLeft}
          y1={height - padBottom}
          x2={renderWidth - padRight}
          y2={height - padBottom}
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="0.5"
        />
        {bars.map((bin, i) => {
          const barHeight = (bin.count / maxCount) * (chartHeight - 4);
          const centerX = padLeft + i * slotWidth + slotWidth / 2;
          const y = height - padBottom - barHeight;
          const isWin = (bin.binStart + bin.binEnd) / 2 > 0;
          return (
            <g key={i}>
              <rect
                x={centerX - barWidth / 2}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={`url(#nc-hist-${isWin ? 'win' : 'loss'})`}
              />
              <text
                x={centerX}
                y={y - 6}
                fontSize="13"
                fill={tokens.textSecondary}
                textAnchor="middle"
                fontFamily="Kanit"
                fontWeight="600"
              >
                {bin.count}
              </text>
              <text
                x={centerX}
                y={height - padBottom + 14}
                fontSize="12"
                fill={tokens.textTertiary}
                textAnchor="middle"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              >
                {fmtBinLabel(bin.binStart, bin.binEnd)}
              </text>
            </g>
          );
        })}
      </>
    );
  })();

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <svg
        viewBox={`0 0 ${renderWidth} ${height}`}
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        <defs>
          <linearGradient id="nc-hist-win" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={tokens.green} stopOpacity="0.95" />
            <stop offset="100%" stopColor={tokens.green} stopOpacity="0.4" />
          </linearGradient>
          <linearGradient id="nc-hist-loss" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={tokens.red} stopOpacity="0.95" />
            <stop offset="100%" stopColor={tokens.red} stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {content}
      </svg>
    </div>
  );
};

export default ProfitHistogram;
